import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { getServices, getTestimonials, getGallery } from '../api';

export default function HomePage() {
  const { settings } = useTheme();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);

  useEffect(() => {
    getServices()
      .then((data) => setServices(data.slice(0, 3)))
      .catch(() => {});
    getTestimonials()
      .then((data) => setTestimonials(data))
      .catch(() => {});
    getGallery()
      .then((data) => setGalleryImages(data.slice(0, 6)))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden">
        {settings.hero_image && (
          <img
            src={settings.hero_image}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-primary)]/80 via-[var(--color-primary)]/60 to-black/70" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <img src="/logo.svg" alt="Skaha Sauna Co." className="h-28 sm:h-36 lg:h-44 mx-auto mb-6" />
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight tracking-tight">
            {settings.hero_title || 'Heat. Lake. Repeat.'}
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
            {settings.hero_subtitle || 'Mobile trailer sauna rentals in Penticton, BC. Delivered lakeside, trailside, or to your backyard.'}
          </p>
          <div className="mt-10">
            <Link
              to="/book"
              className="inline-block px-8 py-4 bg-[var(--color-accent)] text-white text-lg font-semibold rounded-xl shadow-lg hover:opacity-90 transition-all hover:shadow-xl hover:-translate-y-0.5"
            >
              {settings.hero_cta || 'Book Your Session'}
            </Link>
          </div>
        </div>
        {/* Decorative bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[var(--color-bg)] to-transparent" />
      </section>

      {/* Benefits Grid */}
      <section className="py-20 bg-[var(--color-bg)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center text-[var(--color-text)] mb-4">
            Why Skaha Sauna?
          </h2>
          <p className="text-center text-[var(--color-text-light)] mb-14 max-w-2xl mx-auto">
            We bring the heat to you — anywhere, anytime, all year long.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Delivered Anywhere */}
            <div className="bg-[var(--color-bg-alt)] rounded-2xl p-8 text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">Delivered Anywhere</h3>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                Lakeside, trailside, or your own backyard. We tow our custom trailer sauna right to your chosen spot.
              </p>
            </div>

            {/* All-Inclusive */}
            <div className="bg-[var(--color-bg-alt)] rounded-2xl p-8 text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">All-Inclusive</h3>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                Everything you need for the perfect session. Firewood, setup, takedown, and optional add-ons like cold plunge and towels.
              </p>
            </div>

            {/* Year Round */}
            <div className="bg-[var(--color-bg-alt)] rounded-2xl p-8 text-center transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[var(--color-accent)] bg-opacity-10 flex items-center justify-center">
                <svg className="w-8 h-8 text-[var(--color-accent)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-[var(--color-text)] mb-3">Year Round</h3>
              <p className="text-[var(--color-text-light)] leading-relaxed">
                Summer sunset sessions by the lake. Winter snow saunas under the stars. We operate in every season.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      {services.length > 0 && (
        <section className="py-20 bg-[var(--color-bg-alt)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-[var(--color-text)] mb-4">
              Our Sessions
            </h2>
            <p className="text-center text-[var(--color-text-light)] mb-14 max-w-2xl mx-auto">
              Choose the session that fits your schedule and group size.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-[var(--color-bg)] rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow"
                >
                  <h3 className="text-xl font-bold text-[var(--color-text)] mb-2">{service.name}</h3>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-3xl font-extrabold text-[var(--color-accent)]">
                      ${service.price}
                    </span>
                    <span className="text-[var(--color-text-light)] text-sm">
                      / {service.duration_hours}hr{service.duration_hours !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <p className="text-[var(--color-text-light)] mb-6 leading-relaxed">{service.description}</p>
                  <Link
                    to="/book"
                    className="inline-block w-full text-center px-6 py-3 bg-[var(--color-primary)] text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
                  >
                    Book Now
                  </Link>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/services"
                className="text-[var(--color-accent)] font-semibold hover:underline"
              >
                View all services &rarr;
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-20 bg-[var(--color-bg)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-[var(--color-text)] mb-14">
              What People Are Saying
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {testimonials.map((t) => (
                <div
                  key={t.id}
                  className="bg-[var(--color-bg-alt)] rounded-2xl p-8 relative"
                >
                  <svg className="w-8 h-8 text-[var(--color-accent)] opacity-30 absolute top-6 left-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H14.017zM0 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10H0z" />
                  </svg>
                  <p className="text-[var(--color-text)] leading-relaxed mb-6 pt-6 italic">
                    "{t.quote}"
                  </p>
                  <p className="text-[var(--color-accent)] font-semibold">
                    {t.author_name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Preview */}
      {galleryImages.length > 0 && (
        <section className="py-20 bg-[var(--color-bg-alt)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-center text-[var(--color-text)] mb-4">
              The Experience
            </h2>
            <p className="text-center text-[var(--color-text-light)] mb-14 max-w-2xl mx-auto">
              A glimpse at what your sauna session looks like.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {galleryImages.map((img) => (
                <Link
                  key={img.id}
                  to="/gallery"
                  className="relative aspect-[4/3] overflow-hidden rounded-xl group"
                >
                  <img
                    src={img.image_url || img.url}
                    alt={img.caption || ''}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300" />
                  {img.caption && (
                    <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <p className="text-white text-sm font-medium">{img.caption}</p>
                    </div>
                  )}
                </Link>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link
                to="/gallery"
                className="text-[var(--color-accent)] font-semibold hover:underline"
              >
                View full gallery &rarr;
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[var(--color-primary)] to-[var(--color-text)]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Sweat?
          </h2>
          <p className="text-gray-200 text-lg mb-10 max-w-xl mx-auto">
            Book your mobile sauna session today and experience the Okanagan like never before.
          </p>
          <Link
            to="/book"
            className="inline-block px-10 py-4 bg-[var(--color-accent)] text-white text-lg font-semibold rounded-xl shadow-lg hover:opacity-90 transition-all hover:shadow-xl hover:-translate-y-0.5"
          >
            Book Now
          </Link>
        </div>
      </section>
    </div>
  );
}
